import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';

import { Subscription } from 'rxjs';
import {
  BlobUploadRegistry,
  BlobUploadResponse
} from 'src/app/model';
import { FileTypeService } from 'src/app/modules/knowledge-base/services/file-type/file-type.service';
import { ServicecallService } from 'src/app/services/servicecall.service';
import { FileHandle } from '../../../directives/drag.directive';

@Component({
  selector: 'app-file',
  templateUrl: './file.component.html',
  styleUrls: ['./file.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FileComponent
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: FileComponent
    }
  ]
})
export class FileComponent implements OnInit, OnDestroy, ControlValueAccessor {

  isFileTypeSupport = true;
  fileName: string;
  fileUrl: any;
  fileSize: number;
  fileTypeImage: string = null;
  uploadProgress: number;

  private _blobDetails: BlobUploadRegistry = {
    baseUrl: null,
    container: null,
    file: null,
    preSignedUrl : null
  };
  private _touched = false;
  private _disabled = false;
  private _startUpload = false;
  private _uploadedFileUrl: any = null;
  private _validationStatus: ValidationErrors | null;
  private _existDeleteStageCount = 0;
  private _fileDuration: string = null;
  private _file: File = null;

  // Subscription
  // configurationsInfoSubscription: Subscription;
  private _blobUploadSubscription: Subscription;
  private _blobDeleteSubscription: Subscription;
  @Input() folder: string;
  @Input() pageType: string;
  @Input() preSignedUrl : string;
  @Input() isImageOnly: boolean = false;
  imageTypes : string[] =[];

  @Input('startUpload')
  set startUpload(value: boolean) {
    if (value) {
      this._startUpload = value;
      this._uploadedFileUrl ? this._delete() : this._uploadToBlob();
    }
  }

  @Input() image: string;

  @Input() isFileTypeImage: boolean;

  @Input('content')
  set content(value: Partial<BlobUploadResponse>) {
    this.fileName = value?.fileName;
    this.fileUrl = value?.fileUrl;
    this.fileSize = value?.fileSize;
    this._uploadedFileUrl = value?.fileUrl;
    this._setFileTypeImage(value?.fileName);
  }

  @Input() requiredFileSize: number = 1048576; // File size in bytes. Default file size 1 MB

  @Input() requiredFileType: string[];

  @Output() uploadResponse = new EventEmitter<Partial<BlobUploadResponse>>();



  constructor(
    public domSanitizer: DomSanitizer, private _service: ServicecallService, private _fileService: FileTypeService, private dialog: MatDialog
  ) { }

  @HostListener('change', ['$event.target.files']) emitFiles(event: FileList) {
    const file = event && event?.item(0);
    this._file = file;
    this.onChange(this._file);
    this._markAsTouched();
    // this._upload();
  }
  @ViewChild('errorDialog') errorDialogTemplate!: TemplateRef<any>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    this.imageTypes = this._service.getConfiguration('imageTypes') ?? ["jpg","jpeg","png","gif","svg","jfif"];
    this._blobRegistry();
  }

  onChange = (file) => { };

  onTouched = () => { };

  writeValue(file: File = null) {
    this._file = file;
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }
  onSoftDelete() {
    // Updating the flag for deletion of existing image
    if (this._uploadedFileUrl) {
      this._existDeleteStageCount++;
    }
    // Other settings
    this._file = null;
    this.fileUrl = null;
    this.fileName = null;
    this.fileSize = null;
    this.fileTypeImage = null;
    this.isFileTypeSupport = true;
    this.onChange(null);
    this.uploadResponse.emit({
      stage: (this._uploadedFileUrl && this._existDeleteStageCount === 1) ? "exist-delete" : "soft-delete",
      status: true,
      fileDuration: null
    });
  }

  filesDropped(files: FileHandle[]): void {
    if (!files.length) return;
    const file = files[0]?.file;
    if (!this.handleFile(file)) {
      this.resetFileInput();
      return;
    }
  
    this._file = files[0]?.file;
    this.onChange(this._file);
    this._markAsTouched();
    this._upload();
  }
  
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files?.length === 0) return;
  
    const file = input?.files[0];
    if (!this.handleFile(file)) {
      input.value = ''; 
      return;
    }
  
    this._file = file;
    this.onChange(this._file);
    this._markAsTouched();
    this._upload();
  }
  
  handleFile(file: File): boolean {
    this.fileName = file.name;
    this.fileSize = file.size;
  
    if (this.isImageOnly) {
      const ext = file?.name?.split('.')?.pop()?.toLowerCase();
      const isImage = this.imageTypes?.includes(ext || '');
      if (!isImage) {
        this.openErrorDialog(`Allowed file types: ${this.imageTypes.join(', ').toUpperCase()}`);
        return false;
      }
    }
  
    return true;
  }
  
  resetFileInput(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }
  
  openErrorDialog(message: string): void {
    this.dialog.open(this.errorDialogTemplate, { data: message, width: '300px' });
  }

  get imageTypesUpperCase(): string {
    return this.imageTypes?.map(t => t?.toUpperCase())?.join(', ');
  }

  ngOnDestroy(): void {
    // if (this.configurationsInfoSubscription) {
    //   this.configurationsInfoSubscription.unsubscribe();
    // }
    if (this._blobUploadSubscription) {
      this._blobUploadSubscription?.unsubscribe();
    }
    if (this._blobDeleteSubscription) {
      this._blobDeleteSubscription?.unsubscribe();
    }
  }

  _markAsTouched() {
    if (!this._touched) {
      this.onTouched();
      this._touched = true;
    }
  }

  _setDisabledState(disabled: boolean) {
    this._disabled = disabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    this._validationStatus = null;
    const content: File = control?.value;
    // File type validation
    if (content?.type && !this.requiredFileType.includes(content?.type)) {
      this._validationStatus = {
        requiredFileType: true
      };
      return this._validationStatus;
    }
    // File size validation
    if (content?.size && content?.size > this.requiredFileSize) {
      this._validationStatus = {
        requiredFileSize: true
      };
      return this._validationStatus;
    }
    return this._validationStatus;
  }

  private _setFileTypeImage(fileName: string) {
    this.fileTypeImage = this._service.getFileTypeImage(fileName);
  }

  private _upload() {
    let reader = new FileReader();
    reader.readAsDataURL(this._file);
    reader.onload = () => {
      this.fileUrl = reader.result;
    };
    if (this._fileService.VIDEO_AUDIO_SUPPORTED_TYPE.includes(this._file?.type)) {
      // video duration finder
      const video = document.createElement('video');
      video.src = window.URL.createObjectURL(this._file);
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        this._fileDuration = this._durationTimeTransform(video.duration);
        this._setFileDetials();
        // video duration finder end
      }
    } else {
      this._fileDuration = null;
      this._setFileDetials();
    }
  }

  private _setFileDetials() {
    this._blobDetails.file = this._file;
    this.fileName = this._file?.name;
    this.fileSize = this._file?.size;
    this.isFileTypeSupport = this._service.getFileSupportStatus(this._file?.type, this.requiredFileType);
    this._setFileTypeImage(this._file?.name);
    this.uploadResponse.emit({
      stage: "select",
      status: true,
      fileDuration: this._fileDuration
    });
  }

  private _durationTimeTransform(value: any) {
    const hours: number = Math.floor(value / 3600);
    const minutes: number = Math.floor(value / 60);
    const seconds: number = Math.floor(value - minutes * 60);
    return hours + ':' + minutes + ':' + seconds;
  }

  private _blobRegistry() {
    // const attributes: any = ['blobRegistry'];
    // this.configurationsInfoSubscription = this._service.configurationsInfo(attributes).subscribe((result: any) => {
    //   if (result['status']) {
    //     if (result['response'].length) {
    //       const blobRegistry = result['response'].find((item: any) => item?.attribute === 'blobRegistry').values;
    //       this._blobDetails.container = blobRegistry?.container;
    //       this._blobDetails.baseUrl = blobRegistry.idecFace[this.folder];
    //     } else {
    //       this._service.toasterService({ type: 'error', msg: 'Missing configuration for blob' });
    //     }
    //   } else {
    //     this._service.toasterService({ type: 'error', msg: result["response"]["message"] });
    //   }
    // });
    const blobRegistry = this._service.getConfiguration('blobRegistry') ? this._service.getConfiguration('blobRegistry') : [];
    if (blobRegistry) {
      this._blobDetails.container = blobRegistry?.container;
      this._blobDetails.baseUrl = blobRegistry.idecFace[this.folder];
    }
    else {
      this._service.toasterService({ type: 'error', msg: 'Missing configuration for blob' });
    }
  }

  private _uploadToBlob() {
    if (this._file) {
      if(this.preSignedUrl) {
        this._blobDetails.preSignedUrl = this.preSignedUrl;
      }
      this._blobUploadSubscription = this._service.blobUpload(this._blobDetails).subscribe((response: HttpEvent<any>) => {
        if (response?.type == HttpEventType.UploadProgress) {
          this.uploadProgress = Math.round(100 * (response?.loaded / response?.total));
        } else if (response?.type == HttpEventType.Response) {
          this.uploadProgress = null;
          if (response?.status === 201) {
            this.fileUrl = response?.url;
            this.uploadResponse.emit({
              fileUrl: this.fileUrl,
              fileSize: this.fileSize,
              fileName: this.fileName,
              fileDuration: this._fileDuration,
              stage: "upload",
              status: true
            });
          } else {
            this.fileUrl = null;
            this.fileSize = null;
            this.fileName = null;
            this._service.toasterService({ type: 'error', msg: 'File upload failed' });
            this.uploadResponse.emit({
              fileUrl: this.fileUrl,
              fileSize: this.fileSize,
              fileName: this.fileName,
              fileDuration: null,
              stage: "upload",
              status: false
            });
          }
        }
      });
    } else {
      this.fileUrl = null;
      this.fileSize = null;
      this.fileName = null;
      this.uploadResponse.emit({
        fileUrl: this.fileUrl,
        fileSize: this.fileSize,
        fileName: this.fileName,
        fileDuration: null,
        stage: "upload",
        status: false
      });
    }
  }

  private _delete() {
    if (this._uploadedFileUrl) {
      this._blobDeleteSubscription = this._service.blobDelete(this._uploadedFileUrl).subscribe((resp: any) => {
        if (resp.status === 202) {
          this._uploadedFileUrl = null;
        } else {
          this._service.toasterService({ type: 'error', msg: 'File delete failed' });
        }
        // Calling the upload file method only after the deletion of existing file
        this._uploadToBlob();
      });
    }
  }

}
